/**
 * Wireframe Gallery -- Entry Point
 *
 * Wires geometry generators to a categorized CSS grid of canvases.
 * WebGPU primary, Canvas2D fallback. Single rAF loop via registerTick.
 */

import { WireframeGPU, registerTick } from '@wireframe/wireframe-gpu';
import type { WireframeMesh } from '@wireframe/geometry-generators';
import {
  generateOctahedronMesh,
  generateIcosahedronMesh,
  generateVectorEquilibriumMesh,
  generateGeodesicMesh,
  generateTorusMesh,
  generateTriangleMesh,
  generateGrapheneMesh,
  generateTetrahedronMesh,
  generateIvmMesh,
  generateCubeMesh,
  generateDodecahedronMesh,
  generateStarTetrahedronMesh,
  generateBuckyballMesh,
  generateTruncatedOctahedronMesh,
  generateTrefoilKnotMesh,
  generateMobiusStripMesh,
  generatePyramidMesh,
  generateHelixMesh,
  generateDoubleHelixMesh,
  generateDnaMesh,
  generateFibonacciSphereMesh,
  generateVortexMesh,
  generateLorenzMesh,
  generateSierpinskiMesh,
  generateTripleHelixMesh,
  generateTrinityCubeMesh,
  generateGraphiteMesh,
  generateHexGridMesh,
  generateCubicSheetMesh,
  generateCubicLatticeMesh,
  generateTetrahelixMesh,
  generateMetatronCubeMesh,
  generateRhombicDodecahedronMesh,
  generateTesseractMesh,
  generateBorromeanRingsMesh,
  generateKleinBottleMesh,
  generateDiamondMesh,
  generateRosslerMesh,
  generateHyperboloidMesh,
} from '@wireframe/geometry-generators';
import { rotateXYZ, project, renderGlowLines } from '@wireframe/utils-3d';

// ---------------------------------------------------------------------------
// Mesh definitions with categories
// ---------------------------------------------------------------------------

interface MeshDef {
  mesh: WireframeMesh;
  label: string;
  distance: number;
  category: string;
}

const CATEGORIES = ['Trinity', 'Platonic Solids', 'Archimedean & Compounds', 'Parametric Surfaces', 'Spirals & Attractors', 'Lattices & Tilings', 'Higher Dimensions'];

const MESHES: MeshDef[] = [
  // Trinity
  { mesh: generateTrinityCubeMesh({ radius: 1.6 }), label: 'Trinity Cube', distance: 5, category: 'Trinity' },
  { mesh: generateSierpinskiMesh({ radius: 1.8, depth: 3 }), label: 'Sierpinski Tetrahedron', distance: 5, category: 'Trinity' },
  { mesh: generateTripleHelixMesh({ radius: 1.2, pitch: 2.5, turns: 1.5 }), label: 'Triple Helix', distance: 6, category: 'Trinity' },
  { mesh: generateTetrahelixMesh({ radius: 0.7, segments: 12 }), label: 'Tetrahelix', distance: 6, category: 'Trinity' },
  { mesh: generateMetatronCubeMesh({ radius: 1.6 }), label: "Metatron's Cube", distance: 5, category: 'Trinity' },

  // Platonic Solids
  { mesh: generateTetrahedronMesh({ radius: 1.8 }), label: 'Tetrahedron', distance: 5, category: 'Platonic Solids' },
  { mesh: generateCubeMesh({ radius: 1.6 }), label: 'Cube', distance: 5, category: 'Platonic Solids' },
  { mesh: generateOctahedronMesh({ radius: 1.8 }), label: 'Octahedron', distance: 5, category: 'Platonic Solids' },
  { mesh: generateDodecahedronMesh({ radius: 1.8 }), label: 'Dodecahedron', distance: 5, category: 'Platonic Solids' },
  { mesh: generateIcosahedronMesh({ radius: 1.8 }), label: 'Icosahedron', distance: 5, category: 'Platonic Solids' },

  // Archimedean & Compounds
  { mesh: generateVectorEquilibriumMesh({ radius: 1.8 }), label: 'Vector Equilibrium', distance: 5, category: 'Archimedean & Compounds' },
  { mesh: generateTruncatedOctahedronMesh({ radius: 1.8 }), label: 'Truncated Octahedron', distance: 5, category: 'Archimedean & Compounds' },
  { mesh: generateBuckyballMesh({ radius: 1.8 }), label: 'Buckyball', distance: 5, category: 'Archimedean & Compounds' },
  { mesh: generateStarTetrahedronMesh({ radius: 1.8 }), label: 'Star Tetrahedron', distance: 5, category: 'Archimedean & Compounds' },
  { mesh: generateGeodesicMesh({ frequency: 3, radius: 2, cutHeight: 0 }), label: 'Geodesic Dome', distance: 6, category: 'Archimedean & Compounds' },
  { mesh: generateTriangleMesh({ radius: 2, subdivisions: 3 }), label: 'Triangle', distance: 5, category: 'Archimedean & Compounds' },
  { mesh: generatePyramidMesh({ radius: 1.4, height: 2 }), label: 'Pyramid', distance: 5, category: 'Archimedean & Compounds' },
  { mesh: generateRhombicDodecahedronMesh({ radius: 1.7 }), label: 'Rhombic Dodecahedron', distance: 5, category: 'Archimedean & Compounds' },

  // Parametric Surfaces
  { mesh: generateTorusMesh({ segments: 32, tubes: 18, majorRadius: 1.4, minorRadius: 0.5 }), label: 'Torus', distance: 6, category: 'Parametric Surfaces' },
  { mesh: generateTrefoilKnotMesh({ radius: 1.4, tubeRadius: 0.3, segments: 100, tubeSegments: 6 }), label: 'Trefoil Knot', distance: 6, category: 'Parametric Surfaces' },
  { mesh: generateMobiusStripMesh({ radius: 1.5, width: 0.6, segments: 60, strips: 4 }), label: 'Mobius Strip', distance: 6, category: 'Parametric Surfaces' },
  { mesh: generateBorromeanRingsMesh({ radius: 1.4, segments: 64 }), label: 'Borromean Rings', distance: 5, category: 'Parametric Surfaces' },
  { mesh: generateKleinBottleMesh({ radius: 1.0, tubeRadius: 0.32, uSeg: 16, vSeg: 28 }), label: 'Klein Bottle', distance: 5, category: 'Parametric Surfaces' },
  { mesh: generateHyperboloidMesh({ radius: 1.3, height: 2.6, segments: 28, skew: 4 }), label: 'Hyperboloid', distance: 6, category: 'Parametric Surfaces' },

  // Spirals & Attractors
  { mesh: generateHelixMesh({ radius: 1, pitch: 0.6, turns: 4, segments: 120 }), label: 'Helix', distance: 5, category: 'Spirals & Attractors' },
  { mesh: generateDoubleHelixMesh(), label: 'Double Helix', distance: 6, category: 'Spirals & Attractors' },
  { mesh: generateDnaMesh({ radius: 1.2, pitch: 2.8, turns: 1.5, basePairs: 16 }), label: 'DNA', distance: 6, category: 'Spirals & Attractors' },
  { mesh: generateVortexMesh({ radius: 1.6, height: 3, turns: 5, segments: 150 }), label: 'Vortex', distance: 6, category: 'Spirals & Attractors' },
  { mesh: generateLorenzMesh({ radius: 1.8, steps: 3000, dt: 0.005 }), label: 'Lorenz Attractor', distance: 6, category: 'Spirals & Attractors' },
  { mesh: generateRosslerMesh({ radius: 1.8, steps: 4000, dt: 0.02 }), label: 'Rossler Attractor', distance: 6, category: 'Spirals & Attractors' },

  // Lattices & Tilings
  { mesh: generateIvmMesh({ radius: 1.5, cells: 2 }), label: 'IVM Lattice', distance: 5, category: 'Lattices & Tilings' },
  { mesh: generateGrapheneMesh({ radius: 0.3, rings: 3, tilt: 0.05 }), label: 'Graphene', distance: 4, category: 'Lattices & Tilings' },
  { mesh: generateGraphiteMesh({ radius: 0.3, rings: 2, layers: 4, layerSpacing: 2.36 }), label: 'Graphite', distance: 5, category: 'Lattices & Tilings' },
  { mesh: generateHexGridMesh({ radius: 0.3, rings: 4, tilt: 0.04 }), label: 'Hex Grid', distance: 4, category: 'Lattices & Tilings' },
  { mesh: generateCubicSheetMesh({ radius: 0.35, cells: 8, tilt: 0.04 }), label: 'Cubic Sheet', distance: 4, category: 'Lattices & Tilings' },
  { mesh: generateCubicLatticeMesh({ radius: 1.5, cells: 3 }), label: 'Cubic Lattice', distance: 5, category: 'Lattices & Tilings' },
  { mesh: generateFibonacciSphereMesh({ radius: 1.8, points: 120 }), label: 'Fibonacci Sphere', distance: 5, category: 'Lattices & Tilings' },
  { mesh: generateDiamondMesh({ radius: 1.6, cells: 5 }), label: 'Diamond', distance: 5, category: 'Lattices & Tilings' },

  // Higher Dimensions
  { mesh: generateTesseractMesh({ radius: 1.4, w: 3 }), label: 'Tesseract', distance: 5, category: 'Higher Dimensions' },
];

// ---------------------------------------------------------------------------
// Rotation state (shared shape for drag handler)
// ---------------------------------------------------------------------------

interface RotState {
  rx: number;
  ry: number;
  rz: number;
}

// ---------------------------------------------------------------------------
// Cell state
// ---------------------------------------------------------------------------

interface CellState extends RotState {
  canvas: HTMLCanvasElement;
  gpu: WireframeGPU | null;
  ctx2d: CanvasRenderingContext2D | null;
  visible: boolean;
  meshIdx: number;
}

// ---------------------------------------------------------------------------
// Canvas2D fallback renderer
// ---------------------------------------------------------------------------

function renderCanvas2D(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  def: MeshDef,
  rx: number,
  ry: number,
  rz: number,
): void {
  const w = canvas.width;
  const h = canvas.height;
  const cx = w / 2;
  const cy = h / 2;
  const fov = Math.min(w, h) * 0.8;

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#050508';
  ctx.fillRect(0, 0, w, h);

  const lines: { x1: number; y1: number; x2: number; y2: number; z: number }[] = [];

  for (const [a, b] of def.mesh.edges) {
    const ra = rotateXYZ(def.mesh.vertices[a], rx, ry, rz);
    const rb = rotateXYZ(def.mesh.vertices[b], rx, ry, rz);
    const pa = project(ra, fov, def.distance);
    const pb = project(rb, fov, def.distance);
    lines.push({
      x1: cx + pa.x,
      y1: cy + pa.y,
      x2: cx + pb.x,
      y2: cy + pb.y,
      z: (pa.z + pb.z) / 2,
    });
  }

  renderGlowLines(ctx, lines, 4);
}

// ---------------------------------------------------------------------------
// Drag-to-rotate
// ---------------------------------------------------------------------------

let wasDragging = false;

function setupDrag(canvas: HTMLCanvasElement, state: RotState, signal?: AbortSignal): void {
  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  let moved = false;
  const opts = signal ? { signal } : undefined;

  canvas.addEventListener('pointerdown', (e) => {
    dragging = true;
    moved = false;
    lastX = e.clientX;
    lastY = e.clientY;
    canvas.setPointerCapture(e.pointerId);
  }, opts);

  canvas.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) moved = true;
    state.ry += dx * 0.005;
    state.rx += dy * 0.005;
    lastX = e.clientX;
    lastY = e.clientY;
  }, opts);

  canvas.addEventListener('pointerup', () => {
    dragging = false;
    wasDragging = moved;
    requestAnimationFrame(() => {
      wasDragging = false;
    });
  }, opts);
}

// ---------------------------------------------------------------------------
// Solo (fullscreen) view
// ---------------------------------------------------------------------------

let soloGpu: WireframeGPU | null = null;
let soloUnregister: (() => void) | null = null;
let soloDragAbort: AbortController | null = null;
const soloRot: RotState = { rx: 0, ry: 0, rz: 0 };
let currentSoloIdx = -1;

function closeSolo(): void {
  currentSoloIdx = -1;
  const overlay = document.getElementById('solo-view');
  if (overlay) overlay.classList.remove('active');
  if (soloDragAbort) {
    soloDragAbort.abort();
    soloDragAbort = null;
  }
  if (soloUnregister) {
    soloUnregister();
    soloUnregister = null;
  }
  if (soloGpu) {
    soloGpu.destroy();
    soloGpu = null;
  }
}

function computeFov(canvas: HTMLCanvasElement): number {
  return Math.min(canvas.width, canvas.height) * 0.8;
}

async function openSolo(meshIdx: number): Promise<void> {
  const overlay = document.getElementById('solo-view');
  const soloCanvas = document.getElementById('solo-canvas') as HTMLCanvasElement;
  const soloLabel = document.getElementById('solo-label');
  const soloMeta = document.getElementById('solo-meta');
  if (!overlay || !soloCanvas) return;

  closeSolo();
  currentSoloIdx = meshIdx;

  soloRot.rx = 0;
  soloRot.ry = 0;
  soloRot.rz = 0;

  const def = MESHES[meshIdx];
  if (soloLabel) soloLabel.textContent = def.label;
  if (soloMeta) {
    const m = def.mesh.metadata;
    soloMeta.textContent = `V=${m.vertexCount}  E=${m.edgeCount}  F=${m.faceCount}`;
    if (m.formula) {
      soloMeta.textContent += `  ·  ${m.formula}`;
    }
  }
  overlay.classList.add('active');

  // Force reflow so getBoundingClientRect returns actual dimensions
  overlay.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const rect = soloCanvas.getBoundingClientRect();
  soloCanvas.width = Math.round(rect.width * dpr);
  soloCanvas.height = Math.round(rect.height * dpr);

  soloDragAbort = new AbortController();
  setupDrag(soloCanvas, soloRot, soloDragAbort.signal);

  if (navigator.gpu) {
    try {
      const gpu = new WireframeGPU(soloCanvas, {
        mesh: def.mesh,
        distance: def.distance,
        fov: computeFov(soloCanvas),
      });
      await gpu.init();
      soloGpu = gpu;
      gpu.resize(soloCanvas.width, soloCanvas.height);

      let lastT = performance.now();
      soloUnregister = registerTick(() => {
        const now = performance.now();
        const dt = (now - lastT) / 1000;
        lastT = now;
        soloRot.rx += 0.2 * dt;
        soloRot.ry += 0.3 * dt;
        soloRot.rz += 0.05 * dt;
        gpu.setRotation(soloRot.rx, soloRot.ry, soloRot.rz);
        gpu.tick();
      });
      return;
    } catch {
      // Fall through to Canvas2D
    }
  }

  // Canvas2D fallback for solo
  const ctx = soloCanvas.getContext('2d');
  if (!ctx) return;
  let lastT = performance.now();
  soloUnregister = registerTick(() => {
    const now = performance.now();
    const dt = (now - lastT) / 1000;
    lastT = now;
    soloRot.rx += 0.2 * dt;
    soloRot.ry += 0.3 * dt;
    soloRot.rz += 0.05 * dt;
    renderCanvas2D(ctx, soloCanvas, def, soloRot.rx, soloRot.ry, soloRot.rz);
  });
}

// ---------------------------------------------------------------------------
// Build DOM dynamically from MESHES array
// ---------------------------------------------------------------------------

function buildGrid(): void {
  const grid = document.querySelector('.wire-grid')!;
  grid.innerHTML = '';

  for (const category of CATEGORIES) {
    const items = MESHES.filter((m) => m.category === category);
    if (items.length === 0) continue;

    const section = document.createElement('div');
    section.className = 'wire-category';

    const heading = document.createElement('h2');
    heading.className = 'category-heading';
    heading.textContent = category;
    section.appendChild(heading);

    const row = document.createElement('div');
    row.className = 'category-grid';

    for (const item of items) {
      const idx = MESHES.indexOf(item);
      const cell = document.createElement('div');
      cell.className = 'wire-cell';
      cell.dataset.index = String(idx);

      const canvas = document.createElement('canvas');
      cell.appendChild(canvas);

      const info = document.createElement('div');
      info.className = 'wire-info';

      const label = document.createElement('span');
      label.className = 'wire-label';
      label.textContent = item.label;
      info.appendChild(label);

      const meta = document.createElement('span');
      meta.className = 'wire-meta';
      const m = item.mesh.metadata;
      meta.textContent = `V ${m.vertexCount} · E ${m.edgeCount} · F ${m.faceCount}`;
      info.appendChild(meta);

      cell.appendChild(info);
      row.appendChild(cell);
    }

    section.appendChild(row);
    grid.appendChild(section);
  }
}

// ---------------------------------------------------------------------------
// Main init
// ---------------------------------------------------------------------------

(async () => {
  buildGrid();

  const cells = document.querySelectorAll<HTMLDivElement>('.wire-cell');
  const useGPU = !!navigator.gpu;
  const rendererName = useGPU ? 'WebGPU' : 'Canvas2D';
  for (const id of ['renderer-badge', 'renderer-badge-footer']) {
    const el = document.getElementById(id);
    if (el) el.textContent = rendererName;
  }
  for (const id of ['shape-count', 'shape-count-footer']) {
    const el = document.getElementById(id);
    if (el) el.textContent = String(MESHES.length);
  }

  const states: CellState[] = [];

  for (const cell of cells) {
    const idx = parseInt(cell.dataset.index ?? '0', 10);
    const canvas = cell.querySelector('canvas')!;

    const state: CellState = {
      canvas,
      gpu: null,
      ctx2d: null,
      rx: 0,
      ry: 0,
      rz: 0,
      visible: true,
      meshIdx: idx,
    };

    if (useGPU) {
      try {
        const gpu = new WireframeGPU(canvas, {
          mesh: MESHES[idx].mesh,
          distance: MESHES[idx].distance,
          fov: computeFov(canvas),
        });
        await gpu.init();
        state.gpu = gpu;
      } catch {
        state.ctx2d = canvas.getContext('2d');
      }
    } else {
      state.ctx2d = canvas.getContext('2d');
    }

    states.push(state);
    setupDrag(canvas, state);

    cell.addEventListener('click', () => {
      if (wasDragging) return;
      openSolo(idx);
    });
  }

  // --- Resize ---
  function resizeAll() {
    for (const s of states) {
      const rect = s.canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.round(rect.width * dpr);
      const h = Math.round(rect.height * dpr);
      if (s.canvas.width !== w || s.canvas.height !== h) {
        s.canvas.width = w;
        s.canvas.height = h;
        if (s.gpu) s.gpu.resize(w, h);
      }
    }
  }

  const grid = document.querySelector('.wire-grid');
  if (grid) new ResizeObserver(resizeAll).observe(grid);
  resizeAll();

  // --- IntersectionObserver ---
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const state = states.find((s) => s.canvas === entry.target);
        if (state) state.visible = entry.isIntersecting;
      }
    },
    { threshold: 0 },
  );
  for (const s of states) observer.observe(s.canvas);

  // --- Single rAF tick ---
  const AUTO_SPEED = { x: 0.2, y: 0.3, z: 0.05 };
  let lastTime = performance.now();

  registerTick(() => {
    const now = performance.now();
    const dt = (now - lastTime) / 1000;
    lastTime = now;

    for (const s of states) {
      if (!s.visible) continue;

      s.rx += AUTO_SPEED.x * dt;
      s.ry += AUTO_SPEED.y * dt;
      s.rz += AUTO_SPEED.z * dt;

      if (s.gpu) {
        s.gpu.setRotation(s.rx, s.ry, s.rz);
        s.gpu.tick();
      } else if (s.ctx2d) {
        renderCanvas2D(s.ctx2d, s.canvas, MESHES[s.meshIdx], s.rx, s.ry, s.rz);
      }
    }
  });

  // --- Keyboard ---
  document.addEventListener('keydown', (e) => {
    const overlay = document.getElementById('solo-view');
    const soloActive = overlay?.classList.contains('active');

    if (e.key === 'Escape') {
      if (soloActive) {
        closeSolo();
      } else {
        window.location.href = '/';
      }
      return;
    }

    // Arrow key navigation
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const delta = e.key === 'ArrowRight' ? 1 : -1;

      if (soloActive && currentSoloIdx >= 0) {
        // Navigate between shapes in solo view
        const next = (currentSoloIdx + delta + MESHES.length) % MESHES.length;
        openSolo(next);
      } else {
        // Open solo view from gallery — start at first or last
        openSolo(delta === 1 ? 0 : MESHES.length - 1);
      }
    }
  });

  // --- Solo overlay close on backdrop click ---
  const overlay = document.getElementById('solo-view');
  overlay?.addEventListener('click', (e) => {
    if (e.target === overlay) closeSolo();
  });

  const closeBtn = document.getElementById('solo-close');
  closeBtn?.addEventListener('click', closeSolo);
})();
